import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { exportCsv } from '../../utils/csv.js';

/* Der eigene CSV-Erzeuger, der hier stand, ist WEG.
 *
 * Er fasste nur bei Anführungszeichen, Komma und Zeilenumbruch ein, setzte
 * NIE den Formel-Apostroph, schrieb keine Byte-Reihenfolge-Marke und
 * trennte mit Komma. Damit war er dreifach schlechter als der gemeinsame
 * Baustein: eine Zelle mit führendem Gleichheitszeichen kam ungeschützt in
 * die Datei - genau die Sache, gegen die utils/csv.js ausdrücklich härtet -,
 * "Büromaterial Zürich" wurde in Schweizer Excel zu Buchstabensalat, und die
 * ganze Zeile kippte in Spalte A.
 *
 * Elf andere Listenseiten gingen längst über utils/csv.js. Diese eine nicht -
 * und ein Schutz, den man an einer Stelle umgehen kann, ist keiner.
 */

export function ReportBuilderPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [source, setSource] = useState('');
  const [fields, setFields] = useState(new Set());
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState('');
  const [limit, setLimit] = useState(200);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const sources = useQuery({ queryKey: ['finance', 'report-sources'], queryFn: () => financeApi.reportSources().then((r) => r.items) });
  const defs = useQuery({ queryKey: ['finance', 'report-defs'], queryFn: () => financeApi.listReportDefs().then((r) => r.items) });
  const src = useMemo(() => (sources.data || []).find((s) => s.key === source), [sources.data, source]);
  const fieldMeta = (key) => (src?.fields || []).find((f) => f.key === key);

  function pickSource(key) {
    setSource(key);
    const s = (sources.data || []).find((x) => x.key === key);
    setFields(new Set((s?.fields || []).map((f) => f.key)));
    setFilters([]);
    setGroupBy('');
    setResult(null);
  }

  function applyConfig(def) {
    const c = def.config || {};
    pickSource(def.source);
    setTimeout(() => {
      if (c.fields?.length) setFields(new Set(c.fields));
      setFilters(c.filters || []);
      setGroupBy(c.groupBy || '');
      if (c.limit) setLimit(c.limit);
    }, 0);
  }

  const run = useMutation({
    mutationFn: () => financeApi.runReport({
      source,
      fields: groupBy ? undefined : [...fields],
      filters,
      groupBy: groupBy || undefined,
      limit: Number(limit) || 200,
    }),
    onSuccess: (data) => { setError(null); setResult(data); },
    onError: (err) => { setResult(null); setError(err?.message || t('common.loadError')); },
  });

  const save = useMutation({
    mutationFn: (name) => financeApi.saveReportDef({ name, source, config: { fields: [...fields], filters, groupBy: groupBy || undefined, limit: Number(limit) || 200 } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'report-defs'] }),
    onError: (err) => setError(err?.message || t('common.loadError')),
  });
  const del = useMutation({ mutationFn: (id) => financeApi.deleteReportDef(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'report-defs'] }) });

  const addFilter = () => setFilters((f) => [...f, { field: src?.fields?.[0]?.key || '', op: 'eq', value: '' }]);
  const setFilter = (i, patch) => setFilters((f) => f.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const rmFilter = (i) => setFilters((f) => f.filter((_, idx) => idx !== i));

  const columns = (result?.columns || []).map((c) => ({ key: c.key, label: c.label || c.key, align: c.type === 'money' || c.type === 'number' ? 'right' : 'left', render: (r) => <span className={c.type === 'money' || c.type === 'number' ? 'mono num' : ''}>{r[c.key] ?? '-'}</span> }));

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('rb.title')}</h1>
          <p className="muted">{t('rb.subtitle')}</p>
        </div>
      </header>

      <div className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field"><span className="field-label">{t('rb.source')}</span>
            <select className="input" value={source} onChange={(e) => pickSource(e.target.value)}>
              <option value="">-</option>
              {(sources.data || []).map((s) => <option key={s.key} value={s.key}>{t(`rb.src.${s.key}`)}</option>)}
            </select>
          </label>
          <label className="field"><span className="field-label">{t('rb.groupBy')}</span>
            <select className="input" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} disabled={!src}>
              <option value="">{t('rb.noGroup')}</option>
              {(src?.groupable || []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          {!groupBy && (
            <label className="field" style={{ maxWidth: 110 }}><span className="field-label">{t('rb.limit')}</span>
              <input className="input num" type="number" min="1" max="1000" value={limit} onChange={(e) => setLimit(e.target.value)} />
            </label>
          )}
        </div>

        {src && !groupBy && (
          <div className="field">
            <span className="field-label">{t('rb.fields')}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2) var(--space-3)' }}>
              {src.fields.map((f) => (
                <label key={f.key} className="user-admin-role">
                  <input type="checkbox" checked={fields.has(f.key)} onChange={() => setFields((s) => { const n = new Set(s); if (n.has(f.key)) n.delete(f.key); else n.add(f.key); return n; })} />
                  <span>{f.key}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {src && (
          <div className="field">
            <span className="field-label">{t('rb.filters')}</span>
            {filters.map((row, i) => {
              const meta = fieldMeta(row.field);
              const ops = (src.operators?.[meta?.type] || ['eq']);
              return (
                <div key={i} className="field-row" style={{ marginBottom: 'var(--space-2)' }}>
                  <select className="input" value={row.field} onChange={(e) => setFilter(i, { field: e.target.value })}>
                    {src.fields.map((f) => <option key={f.key} value={f.key}>{f.key}</option>)}
                  </select>
                  <select className="input" style={{ maxWidth: 130 }} value={row.op} onChange={(e) => setFilter(i, { op: e.target.value })}>
                    {ops.map((op) => <option key={op} value={op}>{t(`rb.op.${op}`)}</option>)}
                  </select>
                  {meta?.values ? (
                    <select className="input" value={row.value} onChange={(e) => setFilter(i, { value: e.target.value })}>
                      <option value="">-</option>
                      {meta.values.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  ) : (
                    <input className="input" type={meta?.type === 'date' ? 'date' : meta?.type === 'money' || meta?.type === 'number' ? 'number' : 'text'}
                      value={row.value} onChange={(e) => setFilter(i, { value: e.target.value })} placeholder={t('rb.value')} />
                  )}
                  <Button type="button" size="sm" variant="ghost" onClick={() => rmFilter(i)}>✕</Button>
                </div>
              );
            })}
            <Button type="button" size="sm" variant="ghost" onClick={addFilter} disabled={!src}>{t('rb.addFilter')}</Button>
          </div>
        )}

        <div className="page-actions">
          <Button variant="primary" disabled={!source || run.isPending} onClick={() => run.mutate()}>{run.isPending ? `${t('rb.run')}…` : t('rb.run')}</Button>
          <Button variant="secondary" disabled={!source} onClick={() => { const name = window.prompt(t('rb.savePrompt')); if (name) save.mutate(name); }}>{t('rb.save')}</Button>
          <Button variant="ghost" disabled={!result?.rows?.length} onClick={() =>
              exportCsv(
                `report-${source}`,
                result.rows,
                /* Beschriftungen statt roher Spaltennamen: die Datei geht an
                   Menschen, nicht in ein Programm. */
                (result.columns || []).map((c) => ({ key: c.key, label: c.label || c.key })),
              )
            }>{t('rb.exportCsv')}</Button>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </div>

      {result && (
        <Table columns={columns} rows={result.rows} emptyMessage={t('rb.noRows')} />
      )}

      {(defs.data || []).length > 0 && (
        <div style={{ marginTop: 'var(--space-5)' }}>
          <div className="settings-divider">{t('rb.saved')}</div>
          {defs.data.map((d) => (
            <div key={d.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)', marginBottom: 'var(--space-2)' }}>
              <div style={{ flex: 1 }}><strong>{d.name}</strong> <span className="muted text-sm">· {t(`rb.src.${d.source}`)}</span></div>
              <Button size="sm" variant="secondary" onClick={() => applyConfig(d)}>{t('rb.load')}</Button>
              <Button size="sm" variant="ghost" disabled={del.isPending} onClick={() => del.mutate(d.id)}>{t('rb.delete')}</Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
